import { showError, showSuccess } from './toast';

// Standardized error parsing and display for mTLS operations
export const handleApiError = (error: any, context?: string): string => {
  let errorMessage: string;
  
  // Handle dependency conflict errors (409 responses)
  if (error?.error === 'DEPENDENCY_CONFLICT' && error?.blocking_dependencies) {
    return handleDependencyConflictError(error, context);
  }
  
  // Handle validation errors with field details
  if (error?.details && Array.isArray(error.details)) {
    const errorMessages = error.details.map((detail: any) => 
      `• ${detail.field}: ${detail.message}`
    ).join('\n');
    errorMessage = `${error.error || 'Operation failed'}:\n\n${errorMessages}`;
    showError(
      errorMessage,
      `${context} Error`,
      undefined,
      15000
    );
  } else if (error?.validationErrors && Array.isArray(error.validationErrors)) {
    const errorMessages = error.validationErrors.map((e: string) => `• ${e}`).join('\n');
    errorMessage = `Validation failed:\n\n${errorMessages}`;
    showError(errorMessage, `${context} Error`, undefined, 15000);
  } else {
    errorMessage = error?.message || error?.error || 'Unknown error occurred';
    showError(
      errorMessage,
      `${context} Failed`
    );
  }
  
  return errorMessage;
};

// Success message helper
export const handleApiSuccess = (message: string, context?: string) => {
  showSuccess(message, `${context} Success`);
};

// Validation error helper
export const handleValidationErrors = (errors: Record<string, string>, context: string) => {
  const errorMessages = Object.entries(errors)
    .map(([field, message]) => `• ${field}: ${message}`)
    .join('\n');
  
  showError(
    `Validation failed:\n\n${errorMessages}`,
    `${context} Validation Error`,
    undefined,
    10000
  );
};

// Network error helper
export const handleNetworkError = (context: string) => {
  showError(
    'Network connection failed. Please check your connection and try again.',
    `${context} Network Error`,
    undefined,
    8000
  );
};

// Authorization error helper
export const handleAuthError = (context: string) => {
  showError(
    'You do not have permission to perform this action. Please contact your administrator.',
    `${context} Access Denied`,
    undefined,
    10000
  );
};

// Generic dependency conflict error helper (for 409 responses with cleanup instructions)
// Reusable across all mTLS endpoints (organizations, certificates, products, environments)
export const handleDependencyConflictError = (error: any, context?: string): string => {
  // Extract resource information - works for any resource type
  const resourceInfo = error.organization || error.certificate || error.product || error.environment || {};
  const resourceName = resourceInfo.name || resourceInfo.commonName || resourceInfo.productName || resourceInfo.environmentName || 'resource';
  const resourceType = error.organization ? 'organization' : 
                      error.certificate ? 'certificate' : 
                      error.product ? 'product' : 
                      error.environment ? 'environment' : 'resource';
  
  // Build blocking dependencies summary
  const dependencySummary = error.blocking_dependencies?.map((dep: any) => 
    `• ${dep.count} ${dep.type}: ${dep.description}`
  ).join('\n') || '';
  
  // Build cleanup instructions with proper formatting
  const cleanupSteps = error.cleanup_instructions?.map((instruction: any) => {
    let stepText = `${instruction.step}. ${instruction.action}: ${instruction.description}`;
    if (instruction.endpoint && instruction.method) {
      stepText += `\n   API: ${instruction.method} ${instruction.endpoint}`;
    }
    return stepText;
  }).join('\n') || '';
  
  // Build warnings - show all severities but highlight high ones
  const warnings = error.warnings?.map((warning: any) => {
    const icon = warning.severity === 'high' ? '🚨' : 
                warning.severity === 'medium' ? '⚠️' : 'ℹ️';
    return `${icon} ${warning.message}`;
  }).join('\n') || '';
  
  // Build the complete error message
  let fullMessage = `Cannot delete ${resourceName} (${resourceType}) due to existing dependencies:\n\n`;
  
  if (dependencySummary) {
    fullMessage += `Dependencies to resolve:\n${dependencySummary}\n\n`;
  }
  
  if (cleanupSteps) {
    fullMessage += `Required cleanup steps:\n${cleanupSteps}\n\n`;
  }
  
  if (warnings) {
    fullMessage += `Important considerations:\n${warnings}\n\n`;
  }
  
  fullMessage += `Please resolve these dependencies before attempting to delete the ${resourceType}.`;
  
  // Show error with extended timeout for detailed instructions
  showError(
    fullMessage,
    `${context} - Dependencies Found`,
    undefined,
    25000 // Extended timeout for detailed cleanup instructions
  );
  
  return fullMessage;
};